import { 
  ButtonSquareState, 
  ButtonSquareSize, 
  ComputedButtonSquareClasses 
} from './button-square.types';

export const baseButtonSquareStyles = {
  container: [
    'w-[148px]',
    'h-auto',
    'flex',
    'flex-col',
    'items-center',
    'justify-center',
    'font-poppins',
    'p-4',
    'border-[2px]',
    'rounded-lg',
    'transition-all',
    'duration-200',
    'ease-in-out',
    'cursor-pointer',
    'focus:outline-none',
    'select-none',
  ].join(' '),

  iconWrapper: [
    'flex',
    'items-center',
    'justify-center',
  ].join(' '),

  icon: [
    'w-full',
    'h-full',
  ].join(' '),

  title: [
    'text-base',
    'font-semibold',
    'text-center',
    'leading-tight',
  ].join(' '),
};

export const buttonSquareStateStyles = {
  unselected: {
    container: [
      'bg-neutral-gray-50',
      'border-neutral-gray-300',
      'hover:bg-cantera-red-light',
      'hover:border-cantera-red-stepper',
    ].join(' '),
    icon: 'text-gray-600',
    title: 'text-neutral-gray-900',
  },
  selected: {
    container: [
      'bg-cantera-red-light',
      'border-cantera-red-stepper',
      'ring-1',
      'ring-cantera-red-stepper',
    ].join(' '),
    icon: 'text-cantera-red-stepper',
    title: 'text-neutral-gray-900',
  },
};

export const buttonSquareSizeStyles = {
  medium: {
    container: 'w-[148px] min-h-[120px]',
    iconWrapper: '',
    icon: 'w-full h-full',
    title: 'text-base font-semibold',
  },
  large: {
    container: 'w-[168px] min-h-[140px]',
    iconWrapper: '', 
    icon: 'w-7 h-7',
    title: '!text-base !font-semibold',
  },
};

export const disabledButtonSquareStyles = {
  container: [
    'cursor-not-allowed',
    'hover:bg-gray-50',
    'hover:border-gray-300',
    'bg-neutral-gray-100',
    'border-neutral-gray-300',
    'border-[2px]',
  ].join(' '),
  icon: 'text-gray-400',
  title: 'text-gray-900',
};

export const badgeStyles = {
  comingSoon: [
    'absolute',
    'bottom-12',
    'left-1/2',
    '-translate-x-1/2',
    'px-2',
    'py-1',
    'bg-neutral-gray-300',
    'text-neutral-gray-600',
    'text-[10px]',
    'font-semibold',
    'rounded-full',
    'shadow-sm',
    'z-10',
    'w-[74px]',
  ].join(' '),
  new: [
    'absolute',
    'top-2',
    'right-2', 
    'px-2',
    'py-1',
    'bg-green-100',
    'text-green-800',
    'text-xs',
    'font-semibold',
    'rounded-full',
    'border',
    'border-green-300',
  ].join(' '),
};

export function computeButtonSquareClasses(
  state: ButtonSquareState,
  size: ButtonSquareSize,
  disabled: boolean,
  comingSoon: boolean,
  propNew: boolean,
  customClass?: string
): ComputedButtonSquareClasses {
  
  const baseContainer = baseButtonSquareStyles.container;
  const baseIconWrapper = baseButtonSquareStyles.iconWrapper;
  const baseIcon = baseButtonSquareStyles.icon;
  const baseTitle = baseButtonSquareStyles.title;

  const stateStyles = buttonSquareStateStyles[state];
  const sizeStyles = buttonSquareSizeStyles[size];

  let containerClasses = [
    baseContainer,
    stateStyles.container,
    sizeStyles.container
  ];

  if (disabled || comingSoon) {
    containerClasses.push(disabledButtonSquareStyles.container);
  }

  if (customClass) {
    containerClasses.push(customClass);
  }

  const iconWrapperClasses = [
    baseIconWrapper,
    sizeStyles.iconWrapper
  ].join(' ');

  let iconClasses = [
    baseIcon,
    sizeStyles.icon,
    disabled || comingSoon ? disabledButtonSquareStyles.icon : stateStyles.icon
  ].join(' ');

  let titleClasses = [
    baseTitle,
    sizeStyles.title,
    disabled || comingSoon ? disabledButtonSquareStyles.title : stateStyles.title
  ].join(' ');

  const comingSoonBadgeClasses = comingSoon ? badgeStyles.comingSoon : '';
  const newBadgeClasses = propNew && !comingSoon ? badgeStyles.new : '';

  return {
    container: containerClasses.join(' '),
    iconWrapper: iconWrapperClasses,
    icon: iconClasses,
    title: titleClasses,
    comingSoonBadge: comingSoonBadgeClasses,
    newBadge: newBadgeClasses,
  };
}

export function getButtonSquareIconClasses(
  state: ButtonSquareState,
  size: ButtonSquareSize, 
  disabled: boolean
): string {
  const sizeClass = buttonSquareSizeStyles[size].icon;
  const colorClass = disabled 
    ? disabledButtonSquareStyles.icon
    : buttonSquareStateStyles[state].icon;
    
  return `${sizeClass} ${colorClass}`;
}

export function getButtonSquareTitleClasses(
  state: ButtonSquareState,
  size: ButtonSquareSize,
  disabled: boolean
): string {
  const sizeClass = buttonSquareSizeStyles[size].title;
  const colorClass = disabled
    ? disabledButtonSquareStyles.title  
    : buttonSquareStateStyles[state].title;
    
  return `${baseButtonSquareStyles.title} ${sizeClass} ${colorClass}`;
}